import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_TRADING_ROUTES, TRADING_ROUTES } from '@/constants/routes';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import Tooltip from '@/components/ui/Tooltip';
import FullscreenError from '@/components/utils/FullscreenError';
import CreateMultipleCoreTradesModal from '@/components/admin/recommendations/CreateMultipleCoreTradesModal.tsx';
import CreateMultipleEarningTradesModal from '@/components/admin/recommendations/CreateMultipleEarningTradesModal.tsx';
import CreateMultipleSwingTradesModal from '@/components/admin/recommendations/CreateMultipleSwingTradesModal.tsx';
import CreateMultipleLongTermTradesModal from '@/components/admin/recommendations/CreateMultipleLongTermTradesModal.tsx';
import EditCoreRecommendationModal from '@/components/admin/recommendations/EditCoreRecommendationModal.tsx';
import EditEarningsRecommendationModal from '@/components/admin/recommendations/EditEarningsRecommendationModal.tsx';
import EditSwingTradeModal from '@/components/admin/recommendations/EditSwingTradeModal.tsx';
import EditLongTermTradeModal from '@/components/admin/recommendations/EditLongTermTradeModal.tsx';
import AddSwingTradeAdjustementModal from '@/components/admin/recommendations/AddSwingTradeAdjustementModal.tsx';
import AddLongTermTradeAdjustementModal from '@/components/admin/recommendations/AddLongTermTradeAdjustementModal.tsx';
import FinishSwingTradeModal from '@/components/admin/recommendations/FinishSwingTradeModal.tsx';
import FinishLongTermTradeModal from '@/components/admin/recommendations/FinishLongTermTradeModal.tsx';
import ViewSwingTradeAdjustmentsModal from '@/components/admin/recommendations/ViewSwingTradeAdjustmentsModal.tsx';
import ViewLongTermTradeAdjustmentsModal from '@/components/admin/recommendations/ViewLongTermTradeAdjustmentsModal.tsx';
import { faBullseye, faSpinner, faArrowLeft, faEye, faEdit, faTrash, faChartLine, faUpDown, faSort, faSortUp, faSortDown, faSearch, faTimes, faPlus, faCheck, faRocket, faUnlock, faArrowTrendUp, faMemoCircleInfo } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';
import { formatDate, formatCurrency, formatCurrencyCentValue, formatPercentage, calculateOpenStatus } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation';
import type { RecommendationCore, RecommendationEarnings, RecommendationSwingtrade, LongTermRecommendation, SortDirection, CoreRecommendationID, EarningsRecommendationID, SwingTradeID, LongTermRecommendationID } from '@/models';
import type { SwingTradeSort, LongTermTradeSort } from '@/modelsAPI';

// Union type for all recommendation types
type AnyRecommendation = RecommendationCore | RecommendationEarnings | RecommendationSwingtrade | LongTermRecommendation;

// One type-specific table column - a `<td>` on desktop, a labelled row of the mobile card.
interface RecommendationDetail {
  key: string;
  label: string;
  value: ReactNode;
}

const pageSize = 50;

// Helper function to get tab type from URL pathname
function getTabFromPathname(pathname: string): TradingRecommendationType {
  if (pathname.includes('/earnings-recommendations')) {
    return 'EARNINGS';
  }
  if (pathname.includes('/swing-trades')) {
    return 'SWING';
  }
  if (pathname.includes('/long-term-trades')) {
    return 'LONGTERM';
  }
  return 'CORE';
}

// Helper function to get URL path from tab type
function getPathnameFromTab(tab: TradingRecommendationType): string {
  switch (tab) {
    case 'EARNINGS':
      return ADMIN_TRADING_ROUTES.EARNINGS_LIST;
    case 'SWING':
      return ADMIN_TRADING_ROUTES.SWING_LIST;
    case 'LONGTERM':
      return ADMIN_TRADING_ROUTES.LONG_TERM_LIST;
    default:
      return ADMIN_TRADING_ROUTES.CORE_LIST;
  }
}

export default function AdminTradingRecommendationsList() {
  const { isAdmin } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState<AnyRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateCoreTradesModalOpen, setIsCreateCoreTradesModalOpen] = useState(false);
  const [isCreateEarningTradesModalOpen, setIsCreateEarningTradesModalOpen] = useState(false);
  const [isCreateSwingTradesModalOpen, setIsCreateSwingTradesModalOpen] = useState(false);
  const [isCreateLongTermTradesModalOpen, setIsCreateLongTermTradesModalOpen] = useState(false);
  const [isEditCoreModalOpen, setIsEditCoreModalOpen] = useState(false);
  const [isEditEarningsModalOpen, setIsEditEarningsModalOpen] = useState(false);
  const [isEditSwingModalOpen, setIsEditSwingModalOpen] = useState(false);
  const [isEditLongTermModalOpen, setIsEditLongTermModalOpen] = useState(false);
  const [isAddAdjustmentModalOpen, setIsAddAdjustmentModalOpen] = useState(false);
  const [isAddLongTermAdjustmentModalOpen, setIsAddLongTermAdjustmentModalOpen] = useState(false);
  const [isFinishSwingModalOpen, setIsFinishSwingModalOpen] = useState(false);
  const [isFinishLongTermModalOpen, setIsFinishLongTermModalOpen] = useState(false);
  const [isViewAdjustmentsModalOpen, setIsViewAdjustmentsModalOpen] = useState(false);
  const [isViewLongTermAdjustmentsModalOpen, setIsViewLongTermAdjustmentsModalOpen] = useState(false);
  const [editingCoreRecommendation, setEditingCoreRecommendation] = useState<RecommendationCore | null>(null);
  const [editingEarningsRecommendation, setEditingEarningsRecommendation] = useState<RecommendationEarnings | null>(null);
  const [editingSwingTrade, setEditingSwingTrade] = useState<RecommendationSwingtrade | null>(null);
  const [editingLongTermTrade, setEditingLongTermTrade] = useState<LongTermRecommendation | null>(null);
  const [selectedSwingTrade, setSelectedSwingTrade] = useState<RecommendationSwingtrade | null>(null);
  const [selectedLongTermTrade, setSelectedLongTermTrade] = useState<LongTermRecommendation | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [deletingRecommendation, setDeletingRecommendation] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('DESC');
  const [symbolFilter, setSymbolFilter] = useState<string>('');
  const [symbolFilterInput, setSymbolFilterInput] = useState<string>('');

  // Derive activeTab from URL pathname
  const activeTab = getTabFromPathname(location.pathname);

  // Navigate to tab URL (replaces setActiveTab)
  const handleTabChange = (tab: TradingRecommendationType) => {
    if (tab !== activeTab) {
      navigate(getPathnameFromTab(tab));
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when switching tabs
    // SWING/LONGTERM: sort by lastAdjustement (most recently updated first)
    // CORE/EARNINGS: sort by created (newest first)
    setSortField(activeTab === 'SWING' || activeTab === 'LONGTERM' ? 'lastAdjustement' : 'created');
    setSortDirection('DESC'); // Reset to default sort direction
    setSymbolFilter(''); // Reset symbol filter when switching tabs
    setSymbolFilterInput(''); // Reset input field when switching tabs
  }, [activeTab]);

  useEffect(() => {
    fetchRecommendations();
  }, [activeTab, currentPage, sortField, sortDirection, symbolFilter]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      let response: { recommendations?: AnyRecommendation[]; trades?: (RecommendationSwingtrade | LongTermRecommendation)[]; total: number };

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      // Map sort field between SWING/LONGTERM and CORE/EARNINGS if needed
      // All types have 'updated' field from Timestamps interface
      let actualSortField = sortField;
      if (activeTab === 'SWING' || activeTab === 'LONGTERM') {
        // SWING/LONGTERM use startTimestamp instead of startDate
        if (sortField === 'startDate') {
          actualSortField = 'startTimestamp';
        }
      } else {
        // CORE/EARNINGS use startDate instead of startTimestamp
        if (sortField === 'startTimestamp') {
          actualSortField = 'startDate';
        }
        // CORE/EARNINGS don't have lastAdjustement, fall back to 'updated'
        if (sortField === 'lastAdjustement') {
          actualSortField = 'updated';
        }
      }

      if (activeTab === 'CORE') {
        response = await APIRoutes.listCoreRecommendations({
          limit: pageSize,
          next: cursorToken,
          sort: actualSortField as keyof RecommendationCore,
          sortDirection: sortDirection,
          ticker: symbolFilter || undefined
        });
        setRecommendations(response.recommendations || []);
      } else if (activeTab === 'EARNINGS') {
        response = await APIRoutes.listEarningsRecommendations({
          limit: pageSize,
          next: cursorToken,
          sort: actualSortField as keyof RecommendationEarnings,
          sortDirection: sortDirection,
          ticker: symbolFilter || undefined
        });
        setRecommendations(response.recommendations || []);
      } else if (activeTab === 'SWING') {
        response = await APIRoutes.listSwingTrades({
          limit: pageSize,
          next: cursorToken,
          sort: actualSortField as SwingTradeSort,
          sortDirection: sortDirection,
          ticker: symbolFilter || undefined
        });
        setRecommendations(response.trades || []);
      } else if (activeTab === 'LONGTERM') {
        response = await APIRoutes.listLongTermTrades({
          limit: pageSize,
          next: cursorToken,
          sort: actualSortField as LongTermTradeSort,
          sortDirection: sortDirection,
          ticker: symbolFilter || undefined
        });
        setRecommendations(response.trades || []);
      } else {
        setRecommendations([]);
        setTotal(0);
        return;
      }

      setTotal(response.total);
    } catch (err) {
      console.error('Critical error loading trading recommendations list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Tradingideen');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCoreTradesCreated = (newTrades: RecommendationCore[]) => {
    if (activeTab === 'CORE') {
      setRecommendations(prev => [...newTrades, ...prev]);
      setTotal(prev => prev + newTrades.length);
    }
    // Success toast is handled in the modal
  };

  const handleEarningTradesCreated = (newTrades: RecommendationEarnings[]) => {
    if (activeTab === 'EARNINGS') {
      setRecommendations(prev => [...newTrades, ...prev]);
      setTotal(prev => prev + newTrades.length);
    }
    // Success toast is handled in the modal
  };

  const handleSwingTradesCreated = (newTrades: RecommendationSwingtrade[]) => {
    if (activeTab === 'SWING') {
      setRecommendations(prev => [...newTrades, ...prev]);
      setTotal(prev => prev + newTrades.length);
    }
    // Success toast is handled in the modal
  };

  const handleLongTermTradesCreated = (newTrades: LongTermRecommendation[]) => {
    if (activeTab === 'LONGTERM') {
      setRecommendations(prev => [...newTrades, ...prev]);
      setTotal(prev => prev + newTrades.length);
    }
    // Success toast is handled in the modal
  };

  const handleEditRecommendation = (recommendation: AnyRecommendation) => {
    // Determine which modal to open based on the recommendation type
    if (activeTab === 'CORE') {
      setEditingCoreRecommendation(recommendation as RecommendationCore);
      setIsEditCoreModalOpen(true);
    } else if (activeTab === 'EARNINGS') {
      setEditingEarningsRecommendation(recommendation as RecommendationEarnings);
      setIsEditEarningsModalOpen(true);
    } else if (activeTab === 'SWING') {
      setEditingSwingTrade(recommendation as RecommendationSwingtrade);
      setIsEditSwingModalOpen(true);
    } else if (activeTab === 'LONGTERM') {
      setEditingLongTermTrade(recommendation as LongTermRecommendation);
      setIsEditLongTermModalOpen(true);
    }
  };

  const handleCoreRecommendationUpdated = (updatedRecommendation: RecommendationCore) => {
    setRecommendations(prevRecommendations =>
      prevRecommendations.map(recommendation =>
        recommendation.id === updatedRecommendation.id ? updatedRecommendation : recommendation
      )
    );
  };

  const handleEarningsRecommendationUpdated = (updatedRecommendation: RecommendationEarnings) => {
    setRecommendations(prevRecommendations =>
      prevRecommendations.map(recommendation =>
        recommendation.id === updatedRecommendation.id ? updatedRecommendation : recommendation
      )
    );
  };

  const handleSwingTradeUpdated = (updatedSwingTrade: RecommendationSwingtrade) => {
    setRecommendations(prevRecommendations =>
      prevRecommendations.map(recommendation =>
        recommendation.id === updatedSwingTrade.id ? updatedSwingTrade : recommendation
      )
    );
    // Update selectedSwingTrade if it's the same trade
    if (selectedSwingTrade?.id === updatedSwingTrade.id) {
      setSelectedSwingTrade(updatedSwingTrade);
    }
  };

  const handleLongTermTradeUpdated = (updatedLongTermTrade: LongTermRecommendation) => {
    setRecommendations(prevRecommendations =>
      prevRecommendations.map(recommendation =>
        recommendation.id === updatedLongTermTrade.id ? updatedLongTermTrade : recommendation
      )
    );
    // Update selectedLongTermTrade if it's the same trade
    if (selectedLongTermTrade?.id === updatedLongTermTrade.id) {
      setSelectedLongTermTrade(updatedLongTermTrade);
    }
  };

  const handleDeleteRecommendation = async (recommendationId: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Trading-Empfehlung löschen möchten?')) {
      return;
    }

    try {
      setDeletingRecommendation(recommendationId);

      if (activeTab === 'CORE') {
        await APIRoutes.deleteCoreRecommendation(recommendationId as CoreRecommendationID);
      } else if (activeTab === 'EARNINGS') {
        await APIRoutes.deleteEarningsRecommendation(recommendationId as EarningsRecommendationID);
      } else if (activeTab === 'SWING') {
        await APIRoutes.deleteSwingTrade(recommendationId as SwingTradeID);
      } else if (activeTab === 'LONGTERM') {
        await APIRoutes.deleteLongTermTrade(recommendationId as LongTermRecommendationID);
      }

      setRecommendations(prev => prev.filter(recommendation => recommendation.id !== recommendationId));
      setTotal(prev => prev - 1);
      showSuccessToast('Trading-Empfehlung erfolgreich gelöscht');
    } catch (err) {
      console.error('Error deleting trading recommendation:', err);
      showErrorToast('Fehler beim Löschen der Trading-Empfehlung');
      handleAPIError(err);
    } finally {
      setDeletingRecommendation(null);
    }
  };

  const getStateColor = (recommendation: AnyRecommendation): string => {
    // For SwingTrade OPEN state
    if (recommendation.state === 'OPEN') {
      // If it's a Core or Earnings recommendation, calculate substatus
      if ('strikePrice' in recommendation && 'premium' in recommendation && 'winLossDelta' in recommendation) {
        const openStatus = calculateOpenStatus(recommendation.winLossDelta);

        if (openStatus === 'GEHT_DURCH') {
          return 'text-green-600 dark:text-green-400';
        } else if (openStatus === 'WIRD_KNAPP') {
          return 'text-yellow-600 dark:text-yellow-400';
        } else if (openStatus === 'WIRD_EINGEBUCHT') {
          return 'text-red-600 dark:text-red-400';
        }
      }
      // For SwingTrade OPEN or if calculation failed
      return 'text-green-600 dark:text-green-400';
    }

    // For finished states
    if (recommendation.state === 'FINISHED_ASSIGNED') {
      return 'text-orange-600 dark:text-orange-400';
    }

    if (recommendation.state === 'FINISHED_UNASSIGNED' || (recommendation.state as string) === 'FINISHED' || recommendation.state === 'FINISHED_PROFIT') {
      return 'text-lime-400 dark:text-lime-200';
    }

    if (recommendation.state === 'FINISHED_LOSS') {
      return 'text-gray-500 dark:text-gray-400';
    }

    return 'text-lime-400 dark:text-lime-200';
  };

  const getStateLabel = (recommendation: AnyRecommendation): string => {
    // For SwingTrade OPEN state
    if (recommendation.state === 'OPEN') {
      // If it's a Core or Earnings recommendation, calculate substatus
      if ('strikePrice' in recommendation && 'premium' in recommendation && 'winLossDelta' in recommendation) {
        const openStatus = calculateOpenStatus(recommendation.winLossDelta);

        if (openStatus === 'GEHT_DURCH') {
          return 'Geht durch';
        } else if (openStatus === 'WIRD_KNAPP') {
          return 'Wird knapp';
        } else if (openStatus === 'WIRD_EINGEBUCHT') {
          return 'Wird eingebucht';
        }
      }
      // For SwingTrade OPEN or if calculation failed
      return 'Offen';
    }

    // For finished states
    if (recommendation.state === 'FINISHED_ASSIGNED') {
      return 'Eingebucht';
    }

    if (recommendation.state === 'FINISHED_UNASSIGNED' || (recommendation.state as string) === 'FINISHED' || recommendation.state === 'FINISHED_PROFIT') {
      return 'Erfolgreich beendet';
    }

    if (recommendation.state === 'FINISHED_LOSS') {
      return 'Beendet';
    }

    return recommendation.state;
  };

  const getTypeLabel = (type: TradingRecommendationType): string => {
    switch (type) {
      case 'CORE':
        return 'Kernstrategie';
      case 'EARNINGS':
        return 'Earning Trade';
      case 'SWING':
        return 'Swing Trade';
      default:
        return type;
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // New field, start with DESC (newest first)
      setSortField(field);
      setSortDirection('DESC');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return faSort;
    }
    return sortDirection === 'ASC' ? faSortUp : faSortDown;
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Tradingideen nicht verfügbar"
        message={error || 'Die Tradingideen konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchRecommendations}
      />
    );
  }

  const typeColumnCount = activeTab === 'SWING' ? 5 : activeTab === 'LONGTERM' ? 4 : 3;

  const renderSymbol = (recommendation: AnyRecommendation) => (
    <div className="flex items-center">
      <div>
        <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
          <span>{recommendation.symbolTicker}</span>
          {recommendation.boost && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
              <FontAwesomeIcon icon={faRocket} className="w-3 h-3 mr-1" />
              BOOST
            </span>
          )}
          {recommendation.free && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
              <FontAwesomeIcon icon={faUnlock} className="w-3 h-3 mr-1" />
              FREE
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {recommendation.symbol?.currentData?.price !== null ?
            formatCurrency(recommendation.symbol?.currentData?.price || 0, recommendation.symbol?.currentData?.currency || 'USD') :
            'Kein aktueller Preis'
          }
        </div>
      </div>
    </div>
  );

  const renderSector = (recommendation: AnyRecommendation) => (
    <span
      className="block max-w-40 truncate"
      title={recommendation.symbol?.sector ?? ''}
    >
      {recommendation.symbol?.sector ?? '-'}
    </span>
  );

  // Returns null when the payload does not match the active tab - the row then falls back to a
  // single "Unbekannter Typ" cell spanning the type-specific columns.
  const getDetails = (recommendation: AnyRecommendation): RecommendationDetail[] | null => {
    const currency = recommendation.symbol?.currentData?.currency || 'USD';
    const isSwingTrade = activeTab === 'SWING' && 'type' in recommendation && 'adjustements' in recommendation;
    const isLongTermTrade = activeTab === 'LONGTERM' && 'type' in recommendation && 'adjustements' in recommendation;
    const isCoreOrEarnings = (activeTab === 'CORE' || activeTab === 'EARNINGS') && 'strikePrice' in recommendation && 'premium' in recommendation;

    const directionBadge = (type: 'LONG' | 'SHORT') => (
      <div className="flex flex-wrap gap-1">
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          type === 'LONG'
            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
        }`}>
          {type}
        </span>
      </div>
    );

    if (isSwingTrade) {
      const swingTrade = recommendation as RecommendationSwingtrade;
      return [
        { key: 'direction', label: 'Richtung', value: directionBadge(swingTrade.type) },
        { key: 'averagePrice', label: 'Durchschnittspreis', value: formatCurrencyCentValue(swingTrade.averagePrice, currency) },
        { key: 'position', label: 'Akt. Position', value: formatPercentage(swingTrade.currentPosition) },
        {
          key: 'adjustements',
          label: '# Adjustments',
          value: (
            <button
              onClick={() => {
                setSelectedSwingTrade(swingTrade);
                setIsViewAdjustmentsModalOpen(true);
              }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
            >
              {swingTrade.adjustements.length}
            </button>
          )
        },
        { key: 'startDate', label: 'Startdatum', value: formatDate(swingTrade.startTimestamp) }
      ];
    }

    if (isLongTermTrade) {
      const longTermTrade = recommendation as LongTermRecommendation;
      return [
        { key: 'direction', label: 'Richtung', value: directionBadge(longTermTrade.type) },
        { key: 'currentPrice', label: 'Aktueller Preis', value: formatCurrencyCentValue(longTermTrade.currentPrice, currency) },
        {
          key: 'adjustements',
          label: '# Einstiege',
          value: (
            <button
              onClick={() => {
                setSelectedLongTermTrade(longTermTrade);
                setIsViewLongTermAdjustmentsModalOpen(true);
              }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
            >
              {longTermTrade.adjustements.length}
            </button>
          )
        },
        { key: 'startDate', label: 'Startdatum', value: formatDate(longTermTrade.startTimestamp) }
      ];
    }

    if (isCoreOrEarnings) {
      const optionTrade = recommendation as RecommendationCore | RecommendationEarnings;
      return [
        { key: 'strikePrice', label: 'Strike', value: formatCurrencyCentValue(optionTrade.strikePrice, currency) },
        { key: 'premium', label: 'Prämie / Kontrakt', value: formatCurrencyCentValue(optionTrade.premium, currency) },
        {
          key: 'duration',
          label: 'Laufzeit',
          value: (
            <div>
              <div>{formatDate(optionTrade.startDate)} -</div>
              <div>{formatDate(optionTrade.endDate)}</div>
            </div>
          )
        }
      ];
    }

    return null;
  };

  const renderStatus = (recommendation: AnyRecommendation) => (
    <span className={`text-sm font-medium ${getStateColor(recommendation)}`}>
      {getStateLabel(recommendation)}
    </span>
  );

  const renderActions = (recommendation: AnyRecommendation) => {
    const isSwingTrade = activeTab === 'SWING' && 'type' in recommendation && 'adjustements' in recommendation;
    const isLongTermTrade = activeTab === 'LONGTERM' && 'type' in recommendation && 'adjustements' in recommendation;

    return (
      <div className="flex items-center space-x-3">
        <Tooltip text="Öffentliche Ansicht">
          <Link
            to={activeTab === 'CORE' ? TRADING_ROUTES.CORE_RECOMMENDATION_DETAIL(recommendation.id) : activeTab === 'EARNINGS' ? TRADING_ROUTES.EARNINGS_RECOMMENDATION_DETAIL(recommendation.id) : activeTab === 'SWING' ? TRADING_ROUTES.SWING_TRADE_DETAIL(recommendation.id) : TRADING_ROUTES.LONG_TERM_TRADE_DETAIL(recommendation.id)}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer"
          >
            <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
          </Link>
        </Tooltip>
        {activeTab === 'CORE' && isAdmin && (
          <Tooltip text="Admin Ansicht">
            <Link
              to={ADMIN_TRADING_ROUTES.CORE_RECOMMENDATION_DETAIL(recommendation.id)}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 cursor-pointer"
            >
              <FontAwesomeIcon icon={faMemoCircleInfo} className="w-4 h-4" />
            </Link>
          </Tooltip>
        )}
        {activeTab === 'EARNINGS' && isAdmin && (
          <Tooltip text="Admin Ansicht">
            <Link
              to={ADMIN_TRADING_ROUTES.EARNINGS_RECOMMENDATION_DETAIL(recommendation.id)}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 cursor-pointer"
            >
              <FontAwesomeIcon icon={faMemoCircleInfo} className="w-4 h-4" />
            </Link>
          </Tooltip>
        )}
        {activeTab === 'SWING' && isAdmin && (
          <Tooltip text="Admin Ansicht">
            <Link
              to={ADMIN_TRADING_ROUTES.SWING_TRADE_DETAIL(recommendation.id)}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 cursor-pointer"
            >
              <FontAwesomeIcon icon={faMemoCircleInfo} className="w-4 h-4" />
            </Link>
          </Tooltip>
        )}
        {activeTab === 'LONGTERM' && isAdmin && (
          <Tooltip text="Admin Ansicht">
            <Link
              to={ADMIN_TRADING_ROUTES.LONG_TERM_TRADE_DETAIL(recommendation.id)}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 cursor-pointer"
            >
              <FontAwesomeIcon icon={faMemoCircleInfo} className="w-4 h-4" />
            </Link>
          </Tooltip>
        )}
        <Tooltip text={isAdmin ? 'Bearbeiten' : 'Nur für Administratoren'}>
          <button
            onClick={() => handleEditRecommendation(recommendation)}
            disabled={!isAdmin}
            className={`text-gray-600 dark:text-gray-400 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
          </button>
        </Tooltip>
        {isSwingTrade && (recommendation as RecommendationSwingtrade).state === 'OPEN' && (
          <>
            <Tooltip text={isAdmin ? 'Adjustment hinzufügen' : 'Nur für Administratoren'}>
              <button
                onClick={() => {
                  setSelectedSwingTrade(recommendation as RecommendationSwingtrade);
                  setIsAddAdjustmentModalOpen(true);
                }}
                disabled={!isAdmin}
                className={`text-purple-600 dark:text-purple-400 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:text-purple-800 dark:hover:text-purple-300 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
              </button>
            </Tooltip>
            <Tooltip text={isAdmin ? 'Trade beenden' : 'Nur für Administratoren'}>
              <button
                onClick={() => {
                  setSelectedSwingTrade(recommendation as RecommendationSwingtrade);
                  setIsFinishSwingModalOpen(true);
                }}
                disabled={!isAdmin}
                className={`text-green-600 dark:text-green-400 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:text-green-800 dark:hover:text-green-300 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
              </button>
            </Tooltip>
          </>
        )}
        {isLongTermTrade && (recommendation as LongTermRecommendation).state === 'OPEN' && (
          <>
            <Tooltip text={isAdmin ? 'Adjustment hinzufügen' : 'Nur für Administratoren'}>
              <button
                onClick={() => {
                  setSelectedLongTermTrade(recommendation as LongTermRecommendation);
                  setIsAddLongTermAdjustmentModalOpen(true);
                }}
                disabled={!isAdmin}
                className={`text-orange-600 dark:text-orange-400 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:text-orange-800 dark:hover:text-orange-300 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
              </button>
            </Tooltip>
            <Tooltip text={isAdmin ? 'Trade beenden' : 'Nur für Administratoren'}>
              <button
                onClick={() => {
                  setSelectedLongTermTrade(recommendation as LongTermRecommendation);
                  setIsFinishLongTermModalOpen(true);
                }}
                disabled={!isAdmin}
                className={`text-green-600 dark:text-green-400 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:text-green-800 dark:hover:text-green-300 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
              </button>
            </Tooltip>
          </>
        )}
        <Tooltip text={isAdmin ? 'Löschen' : 'Nur für Administratoren'}>
          <button
            onClick={() => handleDeleteRecommendation(recommendation.id)}
            disabled={!isAdmin || deletingRecommendation === recommendation.id}
            className={`text-red-600 dark:text-red-400 disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:text-red-800 dark:hover:text-red-300 cursor-pointer'}`}
          >
            {deletingRecommendation === recommendation.id ? (
              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
            )}
          </button>
        </Tooltip>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center min-w-0">
            <Link
              to="/admin"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Tradingideen ({total})
            </h1>
          </div>
          <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:flex-wrap sm:items-center">
            <button
              onClick={() => setIsCreateCoreTradesModalOpen(true)}
              disabled={!isAdmin}
              title={isAdmin ? 'Neue Kernstrategie-Trades erstellen' : 'Nur für Administratoren'}
              className={`bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center sm:justify-start space-x-2 text-sm sm:text-base transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 cursor-pointer'}`}
            >
              <FontAwesomeIcon icon={faBullseye} className="w-4 h-4 shrink-0" />
              <span className="sm:hidden">Kernstrategie</span>
              <span className="hidden sm:inline">Neue Kernstrategie-Trades</span>
            </button>
            <button
              onClick={() => setIsCreateEarningTradesModalOpen(true)}
              disabled={!isAdmin}
              title={isAdmin ? 'Neue Earning-Trades erstellen' : 'Nur für Administratoren'}
              className={`bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center sm:justify-start space-x-2 text-sm sm:text-base transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700 cursor-pointer'}`}
            >
              <FontAwesomeIcon icon={faChartLine} className="w-4 h-4 shrink-0" />
              <span className="sm:hidden">Earnings</span>
              <span className="hidden sm:inline">Neue Earning-Trades</span>
            </button>
            <button
              onClick={() => setIsCreateSwingTradesModalOpen(true)}
              disabled={!isAdmin}
              title={isAdmin ? 'Neue Swing-Trades erstellen' : 'Nur für Administratoren'}
              className={`bg-purple-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center sm:justify-start space-x-2 text-sm sm:text-base transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-700 cursor-pointer'}`}
            >
              <FontAwesomeIcon icon={faUpDown} className="w-4 h-4 shrink-0" />
              <span className="sm:hidden">Swing</span>
              <span className="hidden sm:inline">Neue Swing-Trades</span>
            </button>
            <button
              onClick={() => setIsCreateLongTermTradesModalOpen(true)}
              disabled={!isAdmin}
              title={isAdmin ? 'Neue Langzeit-Trades erstellen' : 'Nur für Administratoren'}
              className={`bg-orange-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center sm:justify-start space-x-2 text-sm sm:text-base transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-700 cursor-pointer'}`}
            >
              <FontAwesomeIcon icon={faArrowTrendUp} className="w-4 h-4 shrink-0" />
              <span className="sm:hidden">Langzeit</span>
              <span className="hidden sm:inline">Neue Langzeit-Trades</span>
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && !criticalError && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Symbol Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Symbol filtern (z.B. AAPL) + Enter..."
                  value={symbolFilterInput}
                  onChange={(e) => setSymbolFilterInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSymbolFilter(symbolFilterInput);
                      setCurrentPage(1);
                    }
                  }}
                  onBlur={() => {
                    if (symbolFilterInput !== symbolFilter) {
                      setSymbolFilter(symbolFilterInput);
                      setCurrentPage(1);
                    }
                  }}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
                {symbolFilterInput && (
                  <button
                    onClick={() => {
                      setSymbolFilterInput('');
                      setSymbolFilter('');
                      setCurrentPage(1);
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  </button>
                )}
              </div>
            </div>

            <div className="mb-6">
              <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto" aria-label="Tabs">
                <button
                  onClick={() => handleTabChange('CORE')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${
                    activeTab === 'CORE'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Kernstrategie
                </button>
                <button
                  onClick={() => handleTabChange('EARNINGS')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${
                    activeTab === 'EARNINGS'
                      ? 'border-green-500 text-green-600 dark:text-green-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Earnings
                </button>
                <button
                  onClick={() => handleTabChange('SWING')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${
                    activeTab === 'SWING'
                      ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Swing
                </button>
                <button
                  onClick={() => handleTabChange('LONGTERM')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${
                    activeTab === 'LONGTERM'
                      ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Langzeit
                </button>
              </nav>
            </div>
          </div>

        {!loading && recommendations.length === 0 && (
          <div className="text-center py-12">
            <FontAwesomeIcon
              icon={activeTab === 'CORE' ? faBullseye : activeTab === 'EARNINGS' ? faChartLine : faUpDown}
              className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4"
            />
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Keine {getTypeLabel(activeTab)}s gefunden
            </h3>
            <p className="text-gray-500 dark:text-gray-500 mb-6">
              Erstellen Sie Ihren ersten {getTypeLabel(activeTab)}.
            </p>
          </div>
        )}

        {!loading && recommendations.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="hidden md:block overflow-x-scroll">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th
                      onClick={() => handleSort('symbolTicker')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none sticky left-0 z-10 bg-gray-50 dark:bg-gray-700"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Symbol</span>
                        <FontAwesomeIcon icon={getSortIcon('symbolTicker')} className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Branche
                    </th>
                    {activeTab === 'SWING' ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Richtung
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Durchschnittspreis
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Akt. Position
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          # Adjustments
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Startdatum
                        </th>
                      </>
                    ) : activeTab === 'LONGTERM' ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Richtung
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Aktueller Preis
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          # Einstiege
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Startdatum
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Strike
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Prämie / Kontrakt
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Laufzeit
                        </th>
                      </>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky right-0 z-10 bg-gray-50 dark:bg-gray-700">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {recommendations.map((recommendation) => {
                    const details = getDetails(recommendation);

                    return (
                      <tr key={recommendation.id} className="group hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap sticky left-0 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700/50">
                          {renderSymbol(recommendation)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {renderSector(recommendation)}
                        </td>
                        {details !== null ? (
                          details.map((detail) => (
                            <td key={detail.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {detail.value}
                            </td>
                          ))
                        ) : (
                          <td colSpan={typeColumnCount} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Unbekannter Typ
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {renderStatus(recommendation)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 sticky right-0 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700/50">
                          {renderActions(recommendation)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: one card per recommendation - the table only leaves a sliver of scrollable
                width between its two sticky columns on a phone */}
            <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {recommendations.map((recommendation) => {
                const details = getDetails(recommendation);
                const cardDetails: RecommendationDetail[] = [
                  { key: 'sector', label: 'Branche', value: renderSector(recommendation) },
                  ...(details ?? [{ key: 'unknown', label: 'Typ', value: 'Unbekannter Typ' }]),
                  { key: 'status', label: 'Status', value: renderStatus(recommendation) }
                ];

                return (
                  <div key={recommendation.id} className="p-4">
                    {renderSymbol(recommendation)}
                    <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                      {cardDetails.map((detail) => (
                        <div key={detail.key} className="contents">
                          <dt className="text-gray-500 dark:text-gray-400">{detail.label}</dt>
                          <dd className="text-right text-gray-900 dark:text-white flex justify-end items-center">
                            {detail.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50 flex justify-end">
                      {renderActions(recommendation)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={total}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </div>
        )}
          </>
        )}

      {/* Modals */}
      <CreateMultipleCoreTradesModal
        isOpen={isCreateCoreTradesModalOpen}
        onClose={() => setIsCreateCoreTradesModalOpen(false)}
        onTradesCreated={handleCoreTradesCreated}
      />

      <CreateMultipleEarningTradesModal
        isOpen={isCreateEarningTradesModalOpen}
        onClose={() => setIsCreateEarningTradesModalOpen(false)}
        onTradesCreated={handleEarningTradesCreated}
      />

      <CreateMultipleSwingTradesModal
        isOpen={isCreateSwingTradesModalOpen}
        onClose={() => setIsCreateSwingTradesModalOpen(false)}
        onSwingTradesCreated={handleSwingTradesCreated}
      />

      <EditCoreRecommendationModal
        isOpen={isEditCoreModalOpen}
        onClose={() => setIsEditCoreModalOpen(false)}
        recommendation={editingCoreRecommendation}
        onRecommendationUpdated={handleCoreRecommendationUpdated}
      />

      <EditEarningsRecommendationModal
        isOpen={isEditEarningsModalOpen}
        onClose={() => setIsEditEarningsModalOpen(false)}
        recommendation={editingEarningsRecommendation}
        onRecommendationUpdated={handleEarningsRecommendationUpdated}
      />

      <EditSwingTradeModal
        isOpen={isEditSwingModalOpen}
        onClose={() => setIsEditSwingModalOpen(false)}
        swingTrade={editingSwingTrade}
        onSwingTradeUpdated={handleSwingTradeUpdated}
      />

      <AddSwingTradeAdjustementModal
        isOpen={isAddAdjustmentModalOpen}
        onClose={() => {
          setIsAddAdjustmentModalOpen(false);
          setSelectedSwingTrade(null);
        }}
        swingTrade={selectedSwingTrade}
        onAdjustmentAdded={handleSwingTradeUpdated}
      />

      <FinishSwingTradeModal
        isOpen={isFinishSwingModalOpen}
        onClose={() => {
          setIsFinishSwingModalOpen(false);
          setSelectedSwingTrade(null);
        }}
        swingTrade={selectedSwingTrade}
        onSwingTradeFinished={handleSwingTradeUpdated}
      />

      <ViewSwingTradeAdjustmentsModal
        isOpen={isViewAdjustmentsModalOpen}
        onClose={() => {
          setIsViewAdjustmentsModalOpen(false);
          setSelectedSwingTrade(null);
        }}
        swingTrade={selectedSwingTrade}
        onSwingTradeUpdated={handleSwingTradeUpdated}
        isAdmin={isAdmin}
      />

      <CreateMultipleLongTermTradesModal
        isOpen={isCreateLongTermTradesModalOpen}
        onClose={() => setIsCreateLongTermTradesModalOpen(false)}
        onLongTermRecommendationsCreated={handleLongTermTradesCreated}
      />

      <EditLongTermTradeModal
        isOpen={isEditLongTermModalOpen}
        onClose={() => setIsEditLongTermModalOpen(false)}
        longTermTrade={editingLongTermTrade}
        onLongTermRecommendationUpdated={handleLongTermTradeUpdated}
      />

      <AddLongTermTradeAdjustementModal
        isOpen={isAddLongTermAdjustmentModalOpen}
        onClose={() => {
          setIsAddLongTermAdjustmentModalOpen(false);
          setSelectedLongTermTrade(null);
        }}
        longTermTrade={selectedLongTermTrade}
        onAdjustmentAdded={handleLongTermTradeUpdated}
      />

      <FinishLongTermTradeModal
        isOpen={isFinishLongTermModalOpen}
        onClose={() => {
          setIsFinishLongTermModalOpen(false);
          setSelectedLongTermTrade(null);
        }}
        longTermTrade={selectedLongTermTrade}
        onLongTermRecommendationFinished={handleLongTermTradeUpdated}
      />

      <ViewLongTermTradeAdjustmentsModal
        isOpen={isViewLongTermAdjustmentsModalOpen}
        onClose={() => {
          setIsViewLongTermAdjustmentsModalOpen(false);
          setSelectedLongTermTrade(null);
        }}
        longTermTrade={selectedLongTermTrade}
        onLongTermRecommendationUpdated={handleLongTermTradeUpdated}
        isAdmin={isAdmin}
      />
    </>
  );
}
